import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { SessionEntity, SessionCallEntity, PaginatedResponse } from '../types';

// Create API instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'https://localhost:5011',
  headers: {
    'Accept': '*/*',
    'Content-Type': 'application/json'
  }
});

// Store auth functions for dependency injection
let getTelegramAuthData: (() => string) | null = null;
let getUser: (() => any) | null = null;

// Initialize auth data providers (called by AuthProvider)
export const initializeApiAuth = (authDataGetter: () => string, userGetter: () => any) => {
  getTelegramAuthData = authDataGetter;
  getUser = userGetter;
};

// Request interceptor for authentication
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Try Telegram authentication first
    if (getTelegramAuthData && getUser) {
      const authData = getTelegramAuthData();
      const user = getUser();

      if (authData && user) {
        // Add Telegram authentication headers
        config.headers['X-Telegram-Auth'] = authData;
        config.headers['X-Telegram-User-ID'] = user.id.toString();
        console.log('Using Telegram authentication for API request');
        return config;
      }
    }

    // Fallback to basic auth for development/backward compatibility
    const apiToken = process.env.REACT_APP_API_TOKEN;
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;

    if (apiToken) {
      config.headers['Authorization'] = `Basic ${apiToken}`;
      console.log('Using API Token for authentication');
    } else if (username && password) {
      const token = btoa(`${username}:${password}`);
      config.headers['Authorization'] = `Basic ${token}`;
      console.log('Using Basic authentication for API request');
    } else {
      console.warn('No authentication method available');
    }

    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    console.error('API Error:', error.response?.data || error.message);

    // Handle authentication errors
    if (error.response?.status === 401) {
      console.error('Authentication failed - user may need to re-authenticate');
      // You can dispatch an event or call a callback here to handle logout
    }

    // Handle forbidden access
    if (error.response?.status === 403) {
      console.error('Access forbidden - user may not have required permissions');
    }

    return Promise.reject(error);
  }
);

export interface SessionsQuery {
  page?: number;
  pageSize?: number;
  sessionStatus?: number;
  sortDirection?: 'Asc' | 'Desc';
  sortLabel?: string;
  search?: string;
  from?: string;
  to?: string;
}

export interface SessionCallsQuery {
  page?: number;
  pageSize?: number;
  state?: number;
  callStatus?: number;
  sortDirection?: 'Asc' | 'Desc';
  sortLabel?: string;
  search?: string;
  from?: string;
  to?: string;
}

export const sessionsApi = {
  getPaged: async (params: SessionsQuery): Promise<PaginatedResponse<SessionEntity>> => {
    const queryParams: any = {
      Page: params.page || 1,
      PageSize: params.pageSize || 10
    };
    
    if (params.sessionStatus !== undefined) {
      queryParams.SessionStatus = params.sessionStatus;
    }
    if (params.sortDirection) {
      queryParams.SortDirection = params.sortDirection === 'Asc' ? 0 : 1;
    }
    if (params.sortLabel) {
      queryParams.SortLabel = params.sortLabel;
    }
    if (params.search) {
      queryParams.Search = params.search;
    }
    if (params.from) {
      queryParams.From = params.from;
    }
    if (params.to) {
      queryParams.To = params.to;
    }
    
    const response = await api.get('/api/Sessions/paged', { params: queryParams });
    
    // Transform API response to match expected format
    const apiData = response.data;
    return {
      data: apiData.items || [],
      totalCount: apiData.totalCount || 0,
      page: params.page || 1,
      pageSize: params.pageSize || 10,
      totalPages: Math.ceil((apiData.totalCount || 0) / (params.pageSize || 10))
    };
  }
};

export const sessionCallsApi = {
  getBySessionId: async (sessionId: number, params?: SessionCallsQuery): Promise<PaginatedResponse<SessionCallEntity>> => {
    const queryParams: any = {
      Page: params?.page || 1,
      PageSize: params?.pageSize || 10
    };
    
    if (params?.state !== undefined) {
      queryParams.State = params.state;
    }
    if (params?.callStatus !== undefined) {
      queryParams.CallStatus = params.callStatus;
    }
    if (params?.sortDirection) {
      queryParams.SortDirection = params.sortDirection === 'Asc' ? 0 : 1;
    }
    if (params?.sortLabel) {
      queryParams.SortLabel = params.sortLabel;
    }
    if (params?.search) {
      queryParams.Search = params.search;
    }
    if (params?.from) {
      queryParams.From = params.from;
    }
    if (params?.to) {
      queryParams.To = params.to;
    }
    
    const response = await api.get(`/api/SessionCalls/${sessionId}`, { params: queryParams });
    
    // Transform API response to match expected format
    const apiData = response.data;
    return {
      data: apiData.items || [],
      totalCount: apiData.totalCount || 0,
      page: params?.page || 1,
      pageSize: params?.pageSize || 10,
      totalPages: Math.ceil((apiData.totalCount || 0) / (params?.pageSize || 10))
    };
  }
};

export default api;